import type { ReportsIndex, WorkspacePageProps } from '@/types';
import {
  non_working_days_path,
  report_versions_path,
  reports_path,
  tags_path,
} from '@/rails/routes';
import { cn } from '@/lib/utils';
import { Badge } from '@astryxdesign/core/Badge';
import { Banner } from '@astryxdesign/core/Banner';
import { Button } from '@astryxdesign/core/Button';
import { Card } from '@astryxdesign/core/Card';
import { IconButton } from '@astryxdesign/core/IconButton';
import { VStack } from '@astryxdesign/core/VStack';
import { Switch } from '@astryxdesign/core/Switch';
import { router, usePage } from '@inertiajs/react';
import { useDirtyGuard } from '@/components/dirty-guard';
import { History, Plus, Save } from 'lucide-react';
import { useMemo, useState } from 'react';
import DayTabs from './components/day-tabs';
import HistoryDialog from './components/history-dialog';
import ProjectCard from './components/project-card';
import TimelineReport from './components/timeline-report';
import ViewSelector from './components/view-selector';
import type { DraftRow } from './lib/draft';
import {
  entriesFromRows,
  newRow,
  rowsFromEntries,
  rowsHaveChanges,
  timeRangeErrors,
  totalMinutes,
} from './lib/draft';
import { formatDuration, formatShortDate, weekdayName } from './lib/format';
import { getReportView, setReportView, type ReportView } from './lib/view-preference';

const FALLBACK_COLORS = ['#2563EB', '#0D9488', '#7C3AED', '#D97706', '#DB2777', '#0EA5E9'];

type Props = WorkspacePageProps & ReportsIndex;

const Index = () => {
  const {
    auth,
    days,
    selectedDate,
    setting,
    projects,
    tags,
    versions,
    report,
    viewing,
    nonWorkingDaysEnabled,
  } = usePage<Props>().props;

  // Drafts are cached per date so switching days never loses local edits.
  const [drafts, setDrafts] = useState<Record<string, DraftRow[]>>({});
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingCalendar, setIsSavingCalendar] = useState(false);
  const [view, setViewState] = useState<ReportView>(() => getReportView(auth.user.id));

  const selectedDay = days.find((day) => day.date === selectedDate);
  const isViewingOldVersion = viewing != null;
  const canWrite = Boolean(selectedDay && (selectedDay.editable || selectedDay.planned));
  const readOnly = isViewingOldVersion || !canWrite;

  // Legacy entries saved before the catalog was mandatory may lack projectId;
  // resolve them by name against the current catalog.
  const resolveRows = (entries: NonNullable<typeof report>['entries']) =>
    rowsFromEntries(entries).map((row) => {
      if (row.projectId) return row;
      const match = projects.find(
        (project) => project.name.toLowerCase() === row.projectName.toLowerCase()
      );
      return match ? { ...row, projectId: match.id, projectName: match.name } : row;
    });

  const serverRows = useMemo(() => resolveRows(report?.entries ?? []), [report, projects]);
  const viewingRows = useMemo(
    () => (viewing ? resolveRows(viewing.entries) : []),
    [viewing, projects]
  );

  const draft = drafts[selectedDate] ?? serverRows;
  const rows = isViewingOldVersion ? viewingRows : draft;
  const total = totalMinutes(rows);
  const hasMissingProject = rows.some((row) => !row.projectId);
  const rangeErrors = timeRangeErrors(rows);
  const firstRangeError = rangeErrors.values().next().value;
  const isValid = total > 0 && !hasMissingProject && rangeErrors.size === 0;
  const hasChanges = rowsHaveChanges(draft, serverRows);
  const canSave = !readOnly && isValid && hasChanges && !isSaving && !isSavingCalendar;
  // Avisar antes de perder el draft al navegar (cambiar de día, ir a otra página, recargar).
  useDirtyGuard(hasChanges);
  const usedProjectIds = rows.filter((row) => row.projectId != null).map((row) => row.projectId!);
  const availableProjects = projects.filter((project) => !usedProjectIds.includes(project.id));

  const updateDraft = (updater: (rows: DraftRow[]) => DraftRow[]) =>
    setDrafts((all) => ({ ...all, [selectedDate]: updater(all[selectedDate] ?? serverRows) }));

  const setView = (nextView: ReportView) => {
    setViewState(nextView);
    setReportView(auth.user.id, nextView);
  };

  const visitDate = (date: string, version?: number) =>
    router.get(
      reports_path({ date, ...(version ? { version } : {}) }),
      {},
      { preserveState: true, preserveScroll: true }
    );

  const selectDate = (date: string) => {
    if (date !== selectedDate) visitDate(date);
  };

  const save = () => {
    if (canSave) {
      router.post(
        report_versions_path(selectedDate),
        { entries: entriesFromRows(draft) },
        {
          preserveState: true,
          preserveScroll: true,
          onStart: () => setIsSaving(true),
          onFinish: () => setIsSaving(false),
          onSuccess: (page) => {
            // Only drop the local draft when a new version actually landed;
            // validation failures redirect back with an alert and keep the draft.
            const fresh = page.props as unknown as Props;
            if ((fresh.report?.version ?? 0) > (report?.version ?? 0)) {
              setDrafts((all) => {
                const { [selectedDate]: _dropped, ...rest } = all;
                return rest;
              });
            }
          },
        }
      );
    }
  };

  const addTagToActivity = (rowId: string, activityId: string, tagId: string) =>
    updateDraft((current) =>
      current.map((row) =>
        row.id !== rowId
          ? row
          : {
              ...row,
              activities: row.activities.map((activity) =>
                activity.id !== activityId || activity.tags.includes(tagId)
                  ? activity
                  : { ...activity, tags: [...activity.tags, tagId] }
              ),
            }
      )
    );

  const createTag = (
    rowId: string,
    activityId: string,
    label: string,
    onCreated?: (tagId: string) => void
  ) => {
    const clean = label.trim().replace(/\s+/g, ' ');
    if (!clean) return;

    router.post(
      tags_path(),
      { label: clean },
      {
        preserveState: true,
        preserveScroll: true,
        onSuccess: (page) => {
          const created = (page.props as unknown as Props).tags.find(
            (tag) => tag.label.toLowerCase() === clean.toLowerCase()
          );
          if (created) {
            if (onCreated) {
              onCreated(created.id);
            } else {
              addTagToActivity(rowId, activityId, created.id);
            }
          }
        },
      }
    );
  };

  const viewVersion = (version: number) => {
    setIsHistoryOpen(false);
    if (report && version === report.version) {
      visitDate(selectedDate);
    } else {
      visitDate(selectedDate, version);
    }
  };

  const editViewedVersion = () => {
    if (viewing) updateDraft(() => rowsFromEntries(viewing.entries));
    visitDate(selectedDate);
  };

  const projectColor = (row: DraftRow, index: number) =>
    projects.find((project) => project.id === row.projectId)?.color ??
    FALLBACK_COLORS[index % FALLBACK_COLORS.length];

  return (
    <VStack gap={4} width="100%" className="iorio-reports-page">
      <Card padding={0} className="iorio-reports-card">
        <DayTabs days={days} selectedDate={selectedDate} onSelect={selectDate} />

        <div className="iorio-reports-header grid grid-cols-[1fr_auto_1fr] items-center gap-4 px-7 pt-6">
          <div className="justify-self-start">
            <div className="flex flex-wrap items-center gap-2">
              <IconButton
                label="Ver versiones anteriores"
                tooltip="Ver versiones anteriores"
                icon={<History size={18} />}
                isDisabled={versions.length === 0}
                onClick={() => setIsHistoryOpen(true)}
              />
              <ViewSelector value={view} onChange={setView} />
            </div>
          </div>
          <div className="flex flex-col items-center text-center">
            <div className="text-secondary text-xs font-bold tracking-wider uppercase">
              Parte del día
            </div>
            <div className="mt-1 flex items-center gap-2.5">
              <span className="text-primary text-[27px] font-extrabold tracking-tight">
                {formatShortDate(selectedDate)}
              </span>
              {report && !isViewingOldVersion && (
                <Badge label={`v${report.version}`} variant="blue" />
              )}
              {selectedDay?.planned && <Badge label="Planificado" variant="yellow" />}
            </div>
            <div className="text-secondary mt-1 text-sm font-medium">
              {selectedDay ? (selectedDay.isToday ? 'Hoy' : weekdayName(selectedDate)) : ''}
            </div>
          </div>
          <div className="justify-self-end">
            <Button
              variant="primary"
              label={selectedDay?.planned ? 'Guardar plan' : 'Guardar'}
              icon={<Save size={16} />}
              className="iorio-reports-save"
              isLoading={isSaving}
              isDisabled={!canSave}
              tooltip={!readOnly && isValid && !hasChanges ? 'Sin cambios para guardar' : undefined}
              onClick={save}
            />
          </div>
        </div>

        {isViewingOldVersion && (
          <div className="px-7 pt-4">
            <Banner
              status="warning"
              title={`Viendo v${viewing.version} — solo lectura`}
              endContent={
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="primary"
                    label="Editar como nueva versión"
                    onClick={editViewedVersion}
                  />
                  <Button
                    size="sm"
                    label="Volver a la actual"
                    onClick={() => visitDate(selectedDate)}
                  />
                </div>
              }
            />
          </div>
        )}

        <div className="px-7 pt-5 pb-2">
          {view === 'grouped' ? (
            <>
              {rows.map((row, index) => (
                <ProjectCard
                  key={row.id}
                  row={row}
                  color={projectColor(row, index)}
                  tags={tags}
                  projects={projects}
                  usedProjectIds={usedProjectIds}
                  readOnly={readOnly}
                  onChange={(next) =>
                    updateDraft((current) =>
                      current.map((item) => (item.id === row.id ? next : item))
                    )
                  }
                  onRemove={() =>
                    updateDraft((current) => current.filter((item) => item.id !== row.id))
                  }
                  onCreateTag={(activityId, label) => createTag(row.id, activityId, label)}
                />
              ))}

              {!readOnly && availableProjects.length > 0 && (
                <button
                  type="button"
                  onClick={() => updateDraft((current) => [...current, newRow()])}
                  className="iorio-reports-add-project"
                >
                  <Plus size={16} strokeWidth={2.4} />
                  Agregar proyecto
                </button>
              )}
            </>
          ) : (
            <TimelineReport
              rows={rows}
              timeRangeErrors={rangeErrors}
              tags={tags}
              projects={projects}
              readOnly={readOnly}
              projectColor={projectColor}
              onChangeRow={(next) =>
                updateDraft((current) => current.map((item) => (item.id === next.id ? next : item)))
              }
              onRemoveRow={(row) =>
                updateDraft((current) => current.filter((item) => item.id !== row.id))
              }
              onAddRow={(row) => updateDraft((current) => [...current, row])}
              onCreateTag={createTag}
            />
          )}

          {!readOnly && rows.length === 0 && view === 'grouped' && (
            <p className="text-secondary py-6 text-center text-sm">
              Aún no hay proyectos en este parte. Agrega el primero.
            </p>
          )}
        </div>

        <div className="border-border bg-surface flex flex-wrap items-center justify-center gap-4 border-t px-7 py-4">
          <span
            aria-live="polite"
            className={cn(
              'inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-bold',
              isValid ? 'bg-success-muted text-success' : 'bg-error-muted text-error'
            )}
          >
            <span
              className={cn('size-2 rounded-full', isValid ? 'bg-success' : 'bg-error')}
              aria-hidden
            />
            Total del día · {formatDuration(total)}
            {hasMissingProject
              ? ' (elegí un proyecto en cada fila)'
              : firstRangeError
                ? ` (${firstRangeError})`
                : isValid
                  ? ''
                  : view === 'timeline'
                    ? ' (cargá una entrada con horario)'
                    : ' (cargá tiempo en alguna actividad)'}
          </span>
        </div>
      </Card>

      <p className="text-secondary mt-4 text-center text-xs">
        Editable hasta {setting.editableDaysBack} días{' '}
        {nonWorkingDaysEnabled ? 'corridos' : 'hábiles'} hacia atrás · cada guardado con cambios
        crea una versión
      </p>

      <Switch
        label="Habilitar fines de semana y feriados"
        description="Se guarda para tu usuario. Los días con partes cargados siempre se muestran."
        value={nonWorkingDaysEnabled}
        isLoading={isSavingCalendar}
        isDisabled={isSavingCalendar || isSaving}
        onChange={(enabled) =>
          router.visit(non_working_days_path({ date: selectedDate, version: viewing?.version }), {
            method: enabled ? 'post' : 'delete',
            preserveState: true,
            preserveScroll: true,
            onStart: () => setIsSavingCalendar(true),
            onFinish: () => setIsSavingCalendar(false),
          })
        }
      />

      <HistoryDialog
        isOpen={isHistoryOpen}
        onOpenChange={setIsHistoryOpen}
        dateLabel={formatShortDate(selectedDate)}
        versions={versions}
        latestVersion={report?.version ?? null}
        viewingVersion={viewing?.version ?? null}
        onView={viewVersion}
      />
    </VStack>
  );
};

export default Index;
